import { request } from '@/utils/http'

// ===================== 类型定义 =====================

/** 应用分类列表查询请求参数 */
export interface PlatformAppCategoryListRequest {
  name?: string // 分类名称（模糊查询）
  parentId?: string // 父级分类ID
}

/** 应用分类响应结构 */
export interface PlatformAppCategoryVO {
  id: string
  name: string
  nameEn?: string
  status: number
  parentId: string
  parentName?: string
  level: number
  sort: number
  appCount: number // 关联应用数量
  childrenCount: number // 子分类数量
  dateCreated: string
  createdBy?: string // 创建人ID
  createdByName?: string // 创建人姓名
  modifiedBy?: string // 最后修改人ID
  modifiedByName?: string // 最后修改人姓名
  dateModified?: string // 最后修改时间
}

/** 应用批量状态修改请求参数 */
export interface PlatformAppSearchRequest {
  status: number[] // 目标状态：1-上架，2-下架
}

/** 应用列表项返回结构 */
export interface SimplePlatformAppListVO {
  id: string
  name: string
  nameEn?: string
  description: string, // 应用描述（中文）
  descriptionEn: string, // 应用描述（英文）
  status: number, // 应用状态：0-草稿，1-上架，2-下架
  iconFile: string, // 图标文件code
  categoryId: string,
  categoryName: string,
  appUrl: string // 应用链接
  agentTemplateId?: number // Agent应用绑定的模板ID
  ifAgreement: boolean // 0-不需要，1-需要
  agreementTemplateFile?: string // 协议模版文件code
  agreementTemplateFileName?: string // 协议模版文件名称
  ifNeedAccess?: boolean // 是否需要开通：0-不需要，1-需要
  hasUserLimit?: boolean // 是否有用户限制：0-否，1-是
  userLimitCount?: number // 限制用户数量
  dateCreated: string
  dateModified?: string // 最后修改时间
}

/** 应用授权用户（对应 platform-service AppAuthorizedUserVO，CCTOWER-2652） */
export interface ScheduleAuthUser {
  userId: string // 用户ID（雪花ID，后端按字符串下发，禁止转 Number 以免精度丢失）
  username: string // 用户名
  realName: string // 姓名
  email: string // 邮箱
  avatar: string // 头像
  roleType: number // 角色：1管理员 2普通用户
  enabled: number // 是否启用：0禁用 1启用
  authorizeTime: string // 授权时间
}

/** 船期授权用户列表（含上限） */
export interface ScheduleAuthListResult {
  limit: number | null // 授权用户上限；null 表示不限制
  list: ScheduleAuthUser[]
}

/** 申请开通应用请求参数 */
export interface PlatformAppApplyRequest {
  appId: string // 应用ID
  agreementFile: string // 协议文件
  agreementFileName: string // 协议文件名称
}


/** 租户应用开通状态VO */
export interface TenantAppAccessVO {
  appId: string // 应用ID
  status: number, // 应用状态：0-待审核，1-已开通，2-已拒绝，3-已禁用
  auditReason: string // 审核理由
  agreementFile: string // 应用协议文件
  agreementFileName: string // 应用协议文件名称
  appUrl: string, // 应用跳转路由
  agreementTemplateFile: string // 应用协议模板文件
  agreementTemplateFileName: string // 应用协议模板文件名称
  hasUserLimit?: boolean // 是否有用户限制
  userLimitCount?: number // 限制用户数量
}


// ===================== 接口实现 =====================


/**
 * 获取应用分类列表
 * @param params 查询参数
 * @returns 分类列表
 */
export const fetchAppCategoryList = (params: PlatformAppCategoryListRequest) => {
  return request.post<PlatformAppCategoryVO[]>('/platform/appCategory/list', params)
}

/**
 * 查询应用
 * @param params 查询应用参数
 * @returns 查询应用结果
 */
export const searchApp = (params: PlatformAppSearchRequest) => {
  return request.post<SimplePlatformAppListVO[]>('/platform/app/search', params)
}

/**
 * 申请开通
 * @param params 申请开通参数
 * @returns 查询应用结果
 */
export const applyApp = (params: PlatformAppApplyRequest) => {
  return request.post<boolean>('/platform/appAccess/apply', params)
}

/**
 * 租户开通应用信息
 * @returns 查询应用结果
 */
export const tenantAppAccess = () => {
  return request.post<TenantAppAccessVO[]>('/platform/appAccess/tenant')
}

// ===================== 应用授权用户（CCTOWER-2652 M11，通用化至 platform-service） =====================

/**
 * 查询授权用户列表
 * @param appId 应用ID
 * @returns 授权用户列表（含上限）
 */
export const getAuthUserList = (appId: string) => {
  return request.post<ScheduleAuthListResult>('/platform/appAccess/user/list', { appId })
}

/**
 * 变更授权用户（新增 / 移出，移出为物理删除；单次可只传一份名单）
 * @param appId 应用ID
 * @param addUserIds 新增授权的用户ID列表（雪花ID字符串）
 * @param removeUserIds 移出授权的用户ID列表（雪花ID字符串）
 */
export const changeAuthUsers = (appId: string, addUserIds: string[], removeUserIds: string[]) => {
  return request.post<void>('/platform/appAccess/user/change', { appId, addUserIds, removeUserIds })
}
